// screens/AdminReconciliationScreen.js
// ============================================================================
// Wallet Reconciliation Dashboard (Task #8)
// Admin-only view of the daily reconciliation snapshots and a manual trigger
// to fire the function on-demand (e.g., after a Mercury settlement, before
// an investor demo, etc.). The backing scheduled function writes to
// reconciliation_log/{YYYY-MM-DD} at 3 AM UTC; this screen shows the most
// recent snapshot prominently and the last 30 days as a history list.
//
// Anti-tipping-off concern doesn't apply here (this is admin tooling about
// CritCash's own books, not user-facing); so the full snapshot is shown.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Pressable,
  ActivityIndicator, ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { httpsCallable, getFunctions } from 'firebase/functions';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';

const COLORS = {
  bg: '#000000',
  card: '#111111',
  cardAlt: '#0a0a0a',
  border: '#1f1f1f',
  borderLight: '#2a2a2a',
  primary: '#FCD000',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  text: '#ffffff',
  textMuted: '#888888',
  textDim: '#555555',
};

const fmtCents = (cents) => {
  const n = Number(cents) || 0;
  const sign = n < 0 ? '-' : '';
  return `${sign}$${(Math.abs(n) / 100).toFixed(2)}`;
};
const fmtDateTime = (ms) => ms ? new Date(ms).toLocaleString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—';

// ============================================================================
// STRIPE ATLAS FEE WAIVER COUNTDOWN
//
// Stripe Atlas comes with one year of waived standard processing fees (the
// 2.9% + $0.30 per card transaction is what they waive). Until expiration,
// `stripeFeeCents` on every deposit will be 0 — that's correct, not a bug.
// After expiration, fees will start hitting and reconciliation drift dynamics
// will change. This banner counts down so the moment isn't a surprise.
//
// UPDATE THIS DATE if you have a different Atlas activation date:
//   Stripe Dashboard → Settings → Subscription shows the exact end date.
//   Use ISO format (YYYY-MM-DD). Time-of-day is irrelevant; the banner
//   bucketizes by days. Currently set to one year from CritCash's Atlas
//   activation date of 2026-01-23 (confirmed empirically via Stripe API
//   responses on 2026-05-24 still showing fee: 0 on deposits, meaning
//   the waiver was still active 4 months in).
// ============================================================================
const STRIPE_ATLAS_FEE_WAIVER_EXPIRES = '2027-01-23';

function daysUntil(isoDate) {
  const target = new Date(isoDate + 'T23:59:59Z').getTime();
  const ms = target - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export default function AdminReconciliationScreen({ navigation }) {
  const auth = getAuth();
  const functions = getFunctions();

  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [guardLoading, setGuardLoading] = useState(true);

  const [records, setRecords] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [running, setRunning] = useState(false);
  const [errorText, setErrorText] = useState(null);

  // ----- Admin guard (mirrors AdminAmlReviewScreen pattern) -----
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setUser(null);
        setIsAdmin(false);
        setGuardLoading(false);
        navigation.replace('Landing');
        return;
      }
      setUser(u);
      try {
        const token = await u.getIdTokenResult(true);
        const claimAdmin = token?.claims?.role === 'admin';
        const snap = await getDoc(doc(db, 'users', u.uid));
        const docAdmin = snap.exists() && snap.data()?.role === 'admin';
        setIsAdmin(claimAdmin || docAdmin);
      } catch {
        setIsAdmin(false);
      } finally {
        setGuardLoading(false);
      }
    });
    return unsub;
  }, [auth, navigation]);

  // ----- Platform mode (for snapshot-mode mismatch guard) -----
  // Soft-loaded: if it fails we just don't render the mismatch banner.
  const [platformMode, setPlatformMode] = useState(null);
  const fetchPlatformMode = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'getStripeAdminConfig');
      const result = await fn();
      setPlatformMode(result.data?.testMode ? 'test' : 'live');
    } catch (e) {
      console.warn('[reconciliation-admin] config fetch failed:', e.message);
      setPlatformMode(null);
    }
  }, [functions]);

  // ----- History fetch -----
  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    setErrorText(null);
    try {
      const fn = httpsCallable(functions, 'adminGetReconciliationHistory');
      const result = await fn({ limit: 30 });
      setRecords(result.data?.records || []);
    } catch (e) {
      console.error('[reconciliation-admin] history fetch failed:', e);
      setErrorText(`Failed to load history: ${e.message}`);
      setRecords([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [functions]);

  useEffect(() => {
    if (isAdmin) {
      fetchHistory();
      fetchPlatformMode();
    }
  }, [isAdmin, fetchHistory, fetchPlatformMode]);

  // ----- Manual trigger -----
  const runNow = async () => {
    setRunning(true);
    setErrorText(null);
    try {
      const fn = httpsCallable(functions, 'adminRunReconciliation');
      await fn({});
      await fetchHistory(); // pick up the freshly-written record
    } catch (e) {
      console.error('[reconciliation-admin] manual run failed:', e);
      setErrorText(`Run failed: ${e.message}`);
    } finally {
      setRunning(false);
    }
  };

  // ----- Render guards -----
  if (guardLoading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.center}>
          <ActivityIndicator color={COLORS.primary} />
          <Text style={styles.dim}>Checking admin permissions…</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.center}>
          <Ionicons name="lock-closed" size={48} color={COLORS.danger} />
          <Text style={styles.warn}>Admin access required.</Text>
          <TouchableOpacity
            onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}
            style={styles.backBtn}
          >
            <Text style={styles.backBtnText}>Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const latest = records[0] || null;
  const summary = latest?.summary || {};
  const components = latest?.components || {};

  return (
    <SafeAreaView style={styles.safeArea}>
      <Header navigation={navigation} />
      <NavBar navigation={navigation} />
      <ScrollView contentContainerStyle={styles.scroll}>
        {/* Title bar with Run Now */}
        <View style={styles.titleRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.title}>Wallet Reconciliation</Text>
            <Text style={styles.subtitle}>Daily ledger vs. Stripe platform balance</Text>
          </View>
          <Pressable
            onPress={runNow}
            disabled={running}
            style={({ pressed }) => [
              styles.runBtn,
              running && styles.runBtnDisabled,
              pressed && !running && { opacity: 0.85 },
            ]}
          >
            {running ? (
              <ActivityIndicator color={COLORS.bg} />
            ) : (
              <>
                <Ionicons name="refresh" size={16} color={COLORS.bg} />
                <Text style={styles.runBtnText}>Run Now</Text>
              </>
            )}
          </Pressable>
        </View>

        {errorText && (
          <View style={styles.errorBanner}>
            <Ionicons name="alert-circle" size={18} color={COLORS.danger} />
            <Text style={styles.errorText}>{errorText}</Text>
          </View>
        )}

        {/* Stripe Atlas fee waiver countdown */}
        <AtlasFeeWaiverBanner />

        {/* Snapshot-mode mismatch guard: a snapshot from the OTHER Stripe
            mode says nothing about current money — its drift is noise (test
            balances aren't real; live balances aren't visible to test keys). */}
        {latest && platformMode && latest.mode && latest.mode !== platformMode && (
          <View style={styles.mismatchBanner}>
            <Ionicons name="swap-horizontal" size={18} color="#e6b800" />
            <Text style={styles.mismatchText}>
              Latest snapshot is from {String(latest.mode).toUpperCase()} mode, but the platform is now in {String(platformMode).toUpperCase()} mode — its balances and drift don't reflect current state. Hit Run Now for real numbers.
            </Text>
          </View>
        )}

        {/* Latest snapshot card */}
        {latest ? (
          <View style={[styles.card, summary.floorOK ? styles.cardHealthy : styles.cardCritical]}>
            <View style={styles.cardHeader}>
              <View>
                <Text style={styles.cardEyebrow}>Latest snapshot</Text>
                <Text style={styles.cardDate}>{fmtDateTime(latest.computedAt)}</Text>
              </View>
              <View style={[styles.modeChip, latest.mode === 'live' ? styles.modeChipLive : styles.modeChipTest]}>
                <Text style={styles.modeChipText}>{(latest.mode || '?').toUpperCase()}</Text>
              </View>
            </View>

            {/* Floor banner */}
            <View style={summary.floorOK ? styles.floorOk : styles.floorBreach}>
              <Ionicons
                name={summary.floorOK ? 'shield-checkmark' : 'warning'}
                size={20}
                color={summary.floorOK ? COLORS.success : COLORS.danger}
              />
              <Text style={summary.floorOK ? styles.floorOkText : styles.floorBreachText}>
                {summary.floorOK
                  ? `Custodial floor OK — Stripe holds ${fmtCents(summary.actualStripeCents - summary.userOwedAfterPendingCents)} above what we owe users.`
                  : `CRITICAL: Stripe is short ${fmtCents(summary.stripeShortfallCents)} on user funds.`}
              </Text>
            </View>

            {/* Components grid */}
            <View style={styles.componentsGrid}>
              <Component label="User wallets" amount={components.userWallets?.sumCents} sub={`${components.userWallets?.walletCount ?? 0} wallets`} />
              <Component label="In-flight withdrawals" amount={components.inFlightWithdrawals?.amountCents} sub={`${components.inFlightWithdrawals?.withdrawalCount ?? 0} pending`} />
              <Component label="In-flight prize pools" amount={components.eventEarnings?.inFlightPoolCents} sub={`${components.eventEarnings?.inFlightCount ?? 0} tournaments`} />
              <Component label="Earned revenue — events" amount={components.eventEarnings?.earnedRevenueCents} sub={`${components.eventEarnings?.completedCount ?? 0} completed`} />
              <Component
                label="Earned revenue — Turbo (net)"
                amount={components.turboFees?.netRevenueCents}
                sub={`${components.turboFees?.withdrawalCount ?? 0} turbo · ${fmtCents(components.turboFees?.collectedCents)} collected − ${fmtCents(components.turboFees?.absorbedCents)} Stripe fee`}
              />
              <Component label="Absorbed Stripe fees" amount={components.absorbedFees?.sumCents} sub={`${components.absorbedFees?.depositCount ?? 0} deposits`} />
              <Component label="Stripe balance (USD)" amount={components.stripeBalance?.totalCents} sub={`avail ${fmtCents(components.stripeBalance?.availableCents)} • pending ${fmtCents(components.stripeBalance?.pendingCents)}`} highlight />
            </View>

            {/* Derived numbers */}
            <View style={styles.derivedRow}>
              <DerivedRow
                label="Expected Stripe balance"
                value={fmtCents(summary.expectedStripeCents)}
                hint="wallets + in-flight + pools + event revenue + turbo net − absorbed fees"
              />
              <DerivedRow
                label="Actual Stripe balance"
                value={fmtCents(summary.actualStripeCents)}
              />
              <DerivedRow
                label="Drift (expected − actual)"
                value={fmtCents(summary.driftCents)}
                hint={
                  (summary.driftCents || 0) > 0
                    ? `Inferred Mercury settlement: ${fmtCents(summary.inferredSettledToMercuryCents)}`
                    : (summary.driftCents || 0) < 0
                      ? 'Money appeared in Stripe outside tracked paths — investigate.'
                      : 'Books balance exactly.'
                }
                tone={(summary.driftCents || 0) < 0 ? 'warn' : 'normal'}
              />
            </View>
          </View>
        ) : loadingHistory ? (
          <View style={styles.card}>
            <ActivityIndicator color={COLORS.primary} />
            <Text style={styles.dim}>Loading history…</Text>
          </View>
        ) : (
          <View style={styles.card}>
            <Text style={styles.dim}>
              No reconciliation snapshots yet. The scheduled run fires at 3 AM UTC daily, or hit Run Now to generate one immediately.
            </Text>
          </View>
        )}

        {/* History list */}
        <View style={styles.historyHeader}>
          <Text style={styles.historyTitle}>History</Text>
          <Text style={styles.dim}>{records.length} snapshot{records.length === 1 ? '' : 's'}</Text>
        </View>

        {records.slice(1).map((r) => (
          <HistoryRow key={r.dateKey} record={r} />
        ))}

        {records.length <= 1 && !loadingHistory && (
          <Text style={styles.dim}>No prior snapshots to compare against.</Text>
        )}
      </ScrollView>
      <Footer navigation={navigation} />
    </SafeAreaView>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function AtlasFeeWaiverBanner() {
  const days = daysUntil(STRIPE_ATLAS_FEE_WAIVER_EXPIRES);
  const expired = days < 0;
  const expiryLabel = new Date(STRIPE_ATLAS_FEE_WAIVER_EXPIRES + 'T00:00:00Z')
    .toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

  // Bucketize urgency by days remaining. <30 days is "act soon" territory,
  // 30-60 is "start planning," >60 is healthy. Past expiration flips to a
  // different message + danger color regardless of magnitude.
  let tone, label, icon;
  if (expired) {
    tone = 'critical';
    icon = 'alert-circle';
    const daysSince = Math.abs(days);
    label = `EXPIRED ${daysSince === 0 ? 'today' : `${daysSince} day${daysSince === 1 ? '' : 's'} ago`} (${expiryLabel}). Stripe processing fees now apply — your absorbed-fees number will start growing.`;
  } else if (days <= 30) {
    tone = 'critical';
    icon = 'warning';
    label = `Stripe Atlas fee waiver expires in ${days} day${days === 1 ? '' : 's'} (${expiryLabel}). Plan for 2.9% + $0.30 per card transaction to start hitting after that.`;
  } else if (days <= 60) {
    tone = 'warn';
    icon = 'time';
    const months = Math.floor(days / 30);
    label = `Stripe Atlas fee waiver expires in ${days} days (${expiryLabel}) — about ${months} month${months === 1 ? '' : 's'} out.`;
  } else {
    tone = 'healthy';
    icon = 'shield-checkmark';
    const months = Math.floor(days / 30);
    const remainderDays = days % 30;
    label = `Stripe Atlas fee waiver: ${months} month${months === 1 ? '' : 's'}${remainderDays > 0 ? ` ${remainderDays} day${remainderDays === 1 ? '' : 's'}` : ''} remaining (expires ${expiryLabel}). Deposits show $0 absorbed fees until then — that's correct, not a bug.`;
  }

  const bannerStyle =
    tone === 'critical' ? styles.atlasBannerCritical
    : tone === 'warn' ? styles.atlasBannerWarn
    : styles.atlasBannerHealthy;
  const textStyle =
    tone === 'critical' ? styles.atlasBannerTextCritical
    : tone === 'warn' ? styles.atlasBannerTextWarn
    : styles.atlasBannerTextHealthy;
  const iconColor =
    tone === 'critical' ? COLORS.danger
    : tone === 'warn' ? COLORS.warning
    : COLORS.success;

  return (
    <View style={[styles.atlasBannerBase, bannerStyle]}>
      <Ionicons name={icon} size={20} color={iconColor} />
      <Text style={[styles.atlasBannerText, textStyle]}>{label}</Text>
    </View>
  );
}

function Component({ label, amount, sub, highlight }) {
  return (
    <View style={[styles.componentTile, highlight && styles.componentTileHighlight]}>
      <Text style={styles.componentLabel}>{label}</Text>
      <Text style={[styles.componentAmount, highlight && { color: COLORS.primary }]}>{fmtCents(amount)}</Text>
      {sub ? <Text style={styles.componentSub}>{sub}</Text> : null}
    </View>
  );
}

function DerivedRow({ label, value, hint, tone }) {
  return (
    <View style={styles.derivedRowItem}>
      <View style={{ flex: 1 }}>
        <Text style={styles.derivedLabel}>{label}</Text>
        {hint ? <Text style={styles.derivedHint}>{hint}</Text> : null}
      </View>
      <Text style={[styles.derivedValue, tone === 'warn' && { color: COLORS.warning }]}>{value}</Text>
    </View>
  );
}

function HistoryRow({ record }) {
  const s = record.summary || {};
  const stripeTotal = record.components?.stripeBalance?.totalCents;
  return (
    <View style={styles.historyRow}>
      <View style={{ flex: 1 }}>
        <Text style={styles.historyDate}>{record.dateKey}</Text>
        <Text style={styles.historyMeta}>
          {(record.mode || '?').toUpperCase()} • Stripe {fmtCents(stripeTotal)} • drift {fmtCents(s.driftCents)}
        </Text>
      </View>
      <View style={[styles.historyChip, s.floorOK ? styles.historyChipOk : styles.historyChipBad]}>
        <Ionicons
          name={s.floorOK ? 'checkmark' : 'warning'}
          size={14}
          color={s.floorOK ? COLORS.success : COLORS.danger}
        />
        <Text style={[styles.historyChipText, { color: s.floorOK ? COLORS.success : COLORS.danger }]}>
          {s.floorOK ? 'Floor OK' : 'Shortfall'}
        </Text>
      </View>
    </View>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: COLORS.bg },
  scroll: { padding: 16, paddingBottom: 80 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: COLORS.bg, padding: 24 },
  dim: { color: COLORS.textDim, marginTop: 8, fontSize: 13 },
  warn: { color: COLORS.danger, marginTop: 12, fontSize: 16, fontWeight: '600' },
  backBtn: { marginTop: 16, paddingVertical: 10, paddingHorizontal: 24, borderRadius: 6, borderWidth: 1, borderColor: COLORS.border },
  backBtnText: { color: COLORS.text, fontSize: 14 },

  titleRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 16, gap: 12 },
  title: { color: COLORS.text, fontSize: 24, fontWeight: '700' },
  subtitle: { color: COLORS.textMuted, fontSize: 13, marginTop: 2 },
  runBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: COLORS.primary, paddingVertical: 10, paddingHorizontal: 16,
    borderRadius: 6, minWidth: 110, justifyContent: 'center',
  },
  runBtnDisabled: { opacity: 0.6 },
  runBtnText: { color: COLORS.bg, fontSize: 14, fontWeight: '700' },

  errorBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: 'rgba(239,68,68,0.1)', borderColor: COLORS.danger, borderWidth: 1,
    borderRadius: 6, padding: 12, marginBottom: 16,
  },
  errorText: { color: COLORS.danger, fontSize: 13, flex: 1 },

  mismatchBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: 'rgba(230,184,0,0.10)', borderColor: '#e6b800', borderWidth: 1,
    borderRadius: 6, padding: 12, marginBottom: 16,
  },
  mismatchText: { color: '#e6b800', fontSize: 13, flex: 1, lineHeight: 18 },

  atlasBannerBase: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    borderWidth: 1, borderRadius: 6, padding: 12, marginBottom: 16,
  },
  atlasBannerHealthy: { backgroundColor: 'rgba(34,197,94,0.08)', borderColor: COLORS.success },
  atlasBannerWarn:    { backgroundColor: 'rgba(245,158,11,0.10)', borderColor: COLORS.warning },
  atlasBannerCritical:{ backgroundColor: 'rgba(239,68,68,0.12)',  borderColor: COLORS.danger },
  atlasBannerText: { fontSize: 13, flex: 1, lineHeight: 18 },
  atlasBannerTextHealthy: { color: COLORS.success },
  atlasBannerTextWarn:    { color: COLORS.warning },
  atlasBannerTextCritical:{ color: COLORS.danger, fontWeight: '600' },

  card: {
    backgroundColor: COLORS.card, borderColor: COLORS.border, borderWidth: 1,
    borderRadius: 8, padding: 16, marginBottom: 16,
  },
  cardHealthy: { borderColor: COLORS.borderLight },
  cardCritical: { borderColor: COLORS.danger, borderWidth: 2 },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  cardEyebrow: { color: COLORS.textMuted, fontSize: 11, fontWeight: '600', letterSpacing: 0.5, textTransform: 'uppercase' },
  cardDate: { color: COLORS.text, fontSize: 14, marginTop: 2 },

  modeChip: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 4, borderWidth: 1 },
  modeChipLive: { borderColor: COLORS.success, backgroundColor: 'rgba(34,197,94,0.1)' },
  modeChipTest: { borderColor: COLORS.warning, backgroundColor: 'rgba(245,158,11,0.1)' },
  modeChipText: { color: COLORS.text, fontSize: 10, fontWeight: '700', letterSpacing: 0.5 },

  floorOk: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: 'rgba(34,197,94,0.1)', borderColor: COLORS.success, borderWidth: 1,
    borderRadius: 6, padding: 12, marginBottom: 16,
  },
  floorOkText: { color: COLORS.success, fontSize: 13, flex: 1 },
  floorBreach: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: 'rgba(239,68,68,0.15)', borderColor: COLORS.danger, borderWidth: 1,
    borderRadius: 6, padding: 12, marginBottom: 16,
  },
  floorBreachText: { color: COLORS.danger, fontSize: 13, fontWeight: '600', flex: 1 },

  componentsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
  componentTile: {
    backgroundColor: COLORS.cardAlt, borderColor: COLORS.borderLight, borderWidth: 1,
    borderRadius: 6, padding: 10, minWidth: 180, flexBasis: '32%', flexGrow: 1,
  },
  componentTileHighlight: { borderColor: COLORS.primary },
  componentLabel: { color: COLORS.textMuted, fontSize: 11, fontWeight: '600' },
  componentAmount: { color: COLORS.text, fontSize: 18, fontWeight: '700', marginTop: 2 },
  componentSub: { color: COLORS.textDim, fontSize: 11, marginTop: 2 },

  derivedRow: { borderTopColor: COLORS.borderLight, borderTopWidth: 1, paddingTop: 12, gap: 8 },
  derivedRowItem: { flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
  derivedLabel: { color: COLORS.text, fontSize: 13, fontWeight: '600' },
  derivedHint: { color: COLORS.textMuted, fontSize: 11, marginTop: 2 },
  derivedValue: { color: COLORS.text, fontSize: 15, fontWeight: '700', fontVariant: ['tabular-nums'] },

  historyHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, marginBottom: 12 },
  historyTitle: { color: COLORS.text, fontSize: 18, fontWeight: '600' },
  historyRow: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: COLORS.card, borderColor: COLORS.border, borderWidth: 1,
    borderRadius: 6, padding: 12, marginBottom: 8,
  },
  historyDate: { color: COLORS.text, fontSize: 14, fontWeight: '600' },
  historyMeta: { color: COLORS.textMuted, fontSize: 12, marginTop: 2 },
  historyChip: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4, borderWidth: 1,
  },
  historyChipOk: { borderColor: COLORS.success, backgroundColor: 'rgba(34,197,94,0.1)' },
  historyChipBad: { borderColor: COLORS.danger, backgroundColor: 'rgba(239,68,68,0.15)' },
  historyChipText: { fontSize: 11, fontWeight: '700' },
});
